import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { popupOptionType } from "@/types/ModuleOptionsType";
import { columnSize } from "@/utils/layout/columnSize.ts";
import { customSanitize } from "@/utils/customSanitize.ts";
import { popupType } from "@/types/OptionType";

import styles from "./PopUp.module.scss";
import { useAppContext } from "../../context/AppContext";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { ReactSVG } from "react-svg";
import {
  containsScriptTag,
  parseScriptContent,
} from "@/utils/scriptInjection.ts";
import { ExtendedConsentManagement } from "../ExtendedConsentManagement/ExtendedConsentManagement";
import { useLenis } from "lenis/react";
import { FocusTrap } from "@aleksundshantu.com/aunds-utils-react/accessibility";

interface PopUpProps {
  extraClassName?: string;
  popupOptions: popupOptionType;
  popupData: popupType;
}

export const AddProperty = (
  elements: HTMLCollectionOf<HTMLElement>,
  property: string,
  value: string,
) => {
  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    if (element.style) element.style.setProperty(property, value);
  }
};

const PopUp = forwardRef<HTMLDivElement, PopUpProps>(
  ({ extraClassName = "", popupOptions, popupData, ...props }, ref) => {
    const innerRef = useRef<HTMLDivElement | null>(null);

    const {
      columns,
      show_popup,
      box: { show_border_radius, show_box_shadow, show_card_box },
    } = popupOptions ?? {};

    const { openPopup, setopenPopup } = useAppContext();
    const { getIcon, getLabel } = useGlobalContext();

    const { content, type_consent_management, aria_description } =
      popupData ?? {};

    const isScript = containsScriptTag(content);

    const lenis = useLenis();

    useImperativeHandle(ref, () => innerRef.current as HTMLDivElement);

    useEffect(() => {
      if (isScript && openPopup) {
        const tid = setTimeout(() => {
          parseScriptContent(content, "popup", "popup-script");
          clearTimeout(tid);
        }, 100);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isScript, openPopup]);

    useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          setopenPopup(false);
        }
      };

      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const htmlTag = document.documentElement;
      const scrollBarWidth = window.innerWidth - htmlTag.clientWidth;
      const tagsToAddmargin = document.getElementsByClassName(
        "scrollbar-width",
      ) as HTMLCollectionOf<HTMLElement>;
      if (openPopup) {
        if (scrollBarWidth !== 0) {
          AddProperty(
            tagsToAddmargin,
            "width",
            `calc(100% - ${scrollBarWidth}px)`,
          );
          htmlTag.style.marginRight = `${scrollBarWidth}px`;
        }
        lenis?.stop();
      } else {
        AddProperty(tagsToAddmargin, "width", `100%`);
        htmlTag.style.marginRight = `unset`;
        lenis?.start();
      }
    }, [openPopup, lenis]);

    if (!show_popup) return null;

    return (
      <>
        {openPopup && (
          <FocusTrap autoFocus>
            <div
              id="popup"
              ref={innerRef}
              {...props}
              role="dialog"
              aria-modal="true"
              // aria-labelledby="popup-title" // ID modal title ??
              aria-describedby={aria_description ?? "popup-script"}
              //! please dont remove scrollbar-width class
              className={`${styles["pop-up"]} ${extraClassName} scrollbar-width`}
            >
              <button
                onClick={() => {
                  setopenPopup(false);
                }}
                type="button"
                aria-label={getLabel("modal_close")}
                className={`${styles["close-btn"]} navbar-menu-burger-close`}
              >
                <ReactSVG src={getIcon("close")} wrapper={"svg"} />
              </button>
              <div className="container">
                <div className="columns is-centered">
                  <div className={`column ${columnSize(columns)}`}>
                    <ExtendedConsentManagement
                      type_consent_management={type_consent_management}
                    >
                      <div
                        id="popup-script"
                        className={`${styles["popup-content"]}  ${show_card_box ? "is-box" : ""} ${show_border_radius && show_card_box ? "has-border-radius" : ""} ${show_box_shadow && show_card_box ? "has-box-shadow" : ""}`}
                        dangerouslySetInnerHTML={{
                          __html: customSanitize(content),
                        }}
                      />
                    </ExtendedConsentManagement>
                  </div>
                </div>
              </div>
            </div>
          </FocusTrap>
        )}
      </>
    );
  },
);

export default PopUp;

PopUp.displayName = "PopUp";
